import React from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, PieChart, Pie, Cell } from 'recharts';
import { BarChart3, TrendingUp, Users, FileText, Send, Building2 } from 'lucide-react';
import { StatsCard } from '../components/ui/stats-card';
import { LoadingCard } from '../components/ui/loading-spinner';
import { reportsApi, telegramApi, bitrixApi } from '../lib/api';
import { formatCurrency, formatDate } from '../lib/utils';
import { toast } from 'sonner';

const WEEKDAY_OPTIONS = [
  { value: 0, label: 'Понедельник' },
  { value: 1, label: 'Вторник' },
  { value: 2, label: 'Среда' },
  { value: 3, label: 'Четверг' },
  { value: 4, label: 'Пятница' },
  { value: 5, label: 'Суббота' },
  { value: 6, label: 'Воскресенье' },
];

const CHANNEL_LABELS = { telegram: 'Telegram', bitrix24: 'Bitrix24' };

const SCHEDULE_STATUS_LABELS = {
  ok: 'успешно',
  partial: 'частично (не все каналы)',
  error: 'ошибка',
  no_channels: 'нет подключённых каналов',
  skipped_inactive: 'компания неактивна',
};

export default function Reports() {
  const queryClient = useQueryClient();
  const [filterType, setFilterType] = React.useState('all'); // all, manager, project
  const [selectedManager, setSelectedManager] = React.useState('');
  const [selectedProject, setSelectedProject] = React.useState('');
  const [dateFrom, setDateFrom] = React.useState(() => {
    const d = new Date();
    d.setDate(d.getDate() - 30);
    return d.toISOString().split('T')[0];
  });
  const [dateTo, setDateTo] = React.useState(() => new Date().toISOString().split('T')[0]);

  const { data: dashboardStats, isLoading: statsLoading } = useQuery({
    queryKey: ['dashboard-stats'],
    queryFn: () => reportsApi.getDashboard().then(res => res.data),
  });

  const { data: monthlyRevenue, isLoading: revenueLoading } = useQuery({
    queryKey: ['monthly-revenue'],
    queryFn: () => reportsApi.getMonthlyRevenue().then(res => res.data),
  });

  const { data: projectDistribution, isLoading: distributionLoading } = useQuery({
    queryKey: ['project-distribution'],
    queryFn: () => reportsApi.getProjectDistribution().then(res => res.data),
  });

  const { data: clientRevenue, isLoading: clientRevenueLoading } = useQuery({
    queryKey: ['client-revenue'],
    queryFn: () => reportsApi.getClientRevenue({ limit: 10 }).then(res => res.data),
  });

  // Загрузка проектов для фильтра
  const { data: projectsData } = useQuery({
    queryKey: ['projects-list'],
    queryFn: () => reportsApi.getProjectsList(),
  });

  // Детальная аналитика в зависимости от фильтра
  const { data: detailedAnalytics, isLoading: analyticsLoading } = useQuery({
    queryKey: ['detailed-analytics', filterType, selectedManager, selectedProject, dateFrom, dateTo],
    queryFn: () => {
      if (filterType === 'manager' && selectedManager) {
        return reportsApi.getByManager(selectedManager, dateFrom, dateTo);
      } else if (filterType === 'project' && selectedProject) {
        return reportsApi.getProjectReport(selectedProject, dateFrom, dateTo);
      } else {
        return reportsApi.getSummary(dateFrom, dateTo);
      }
    },
    enabled: !!filterType && !!dateFrom && !!dateTo,
  });

  // Churn Analysis данные
  const [churnPeriod, setChurnPeriod] = React.useState('monthly');
  const [churnMonths, setChurnMonths] = React.useState(12);
  
  const { data: churnData, isLoading: churnLoading } = useQuery({
    queryKey: ['churn-analysis', churnPeriod, churnMonths],
    queryFn: () => reportsApi.getChurnAnalysis(churnPeriod, churnMonths),
  });

  const sendReportMutation = useMutation({
    mutationFn: telegramApi.sendDailyReport,
    onSuccess: () => {
      toast.success('Ежедневный отчёт отправлен в Telegram');
    },
    onError: (error) => {
      toast.error(error.response?.data?.detail || 'Ошибка отправки отчёта');
    },
  });

  const sendBitrixReportMutation = useMutation({
    mutationFn: bitrixApi.sendDailyReport,
    onSuccess: () => {
      toast.success('Ежедневный отчёт отправлен в Bitrix24');
    },
    onError: (error) => {
      toast.error(error.response?.data?.detail || 'Ошибка отправки отчёта');
    },
  });

  // Расписание автоматической отправки — день недели + время (МСК) + каналы
  const [scheduleEnabled, setScheduleEnabled] = React.useState(false);
  const [scheduleDay, setScheduleDay] = React.useState(0);
  const [scheduleTime, setScheduleTime] = React.useState('09:00');
  const [selectedChannels, setSelectedChannels] = React.useState([]);

  const { data: scheduleData } = useQuery({
    queryKey: ['report-schedule'],
    queryFn: () => reportsApi.getSchedule().then((res) => res.data),
  });

  React.useEffect(() => {
    if (scheduleData) {
      setScheduleEnabled(scheduleData.enabled);
      setScheduleDay(scheduleData.day_of_week);
      setScheduleTime(`${String(scheduleData.hour).padStart(2, '0')}:${String(scheduleData.minute).padStart(2, '0')}`);
      // Пустой сохранённый выбор (в т.ч. расписания, сохранённые до появления
      // выбора канала) по умолчанию — все сейчас доступные каналы, чтобы
      // поведение не поменялось молча для тех, кто уже настроил расписание.
      setSelectedChannels(
        scheduleData.channels && scheduleData.channels.length
          ? scheduleData.channels
          : scheduleData.available_channels || []
      );
    }
  }, [scheduleData]);

  const toggleScheduleChannel = (channel) => {
    setSelectedChannels((prev) => (
      prev.includes(channel) ? prev.filter((c) => c !== channel) : [...prev, channel]
    ));
  };

  const saveScheduleMutation = useMutation({
    mutationFn: () => {
      const [hour, minute] = scheduleTime.split(':').map(Number);
      return reportsApi.saveSchedule({ enabled: scheduleEnabled, day_of_week: scheduleDay, hour, minute, channels: selectedChannels });
    },
    onSuccess: () => {
      toast.success('Расписание сохранено');
      queryClient.invalidateQueries({ queryKey: ['report-schedule'] });
    },
    onError: (error) => {
      toast.error(error.response?.data?.detail || 'Ошибка сохранения расписания');
    },
  });

  const COLORS = ['#3B82F6', '#EF4444', '#F59E0B', '#10B981', '#8B5CF6', '#F97316'];

  function getStatusLabel(status) {
    const statusMap = {
      planning: 'Планирование',
      in_progress: 'В процессе',
      review: 'На проверке',
      completed: 'Завершён',
      on_hold: 'Приостановлен',
      cancelled: 'Отменён',
    };
    return statusMap[status] || status;
  }

  if (statsLoading) {
    return (
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <h1 className="text-2xl font-bold text-gray-900">Отчеты</h1>
        </div>
        <LoadingCard />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Заголовок и действия */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Отчеты и аналитика</h1>
          <p className="text-gray-600 mt-1">
            Детальная статистика по проектам, клиентам и финансам
          </p>
        </div>
        
        <div className="flex flex-wrap gap-3">
          <button
            onClick={() => sendReportMutation.mutate()}
            disabled={sendReportMutation.isPending}
            className="bg-green-600 text-white px-4 py-2 rounded-lg flex items-center space-x-2 hover:bg-green-700 transition-colors disabled:opacity-50"
            data-testid="send-telegram-report-btn"
          >
            <Send className="w-5 h-5" />
            <span>
              {sendReportMutation.isPending ? 'Отправляем...' : 'Отправить отчёт в Telegram'}
            </span>
          </button>
          <button
            onClick={() => sendBitrixReportMutation.mutate()}
            disabled={sendBitrixReportMutation.isPending}
            className="bg-blue-600 text-white px-4 py-2 rounded-lg flex items-center space-x-2 hover:bg-blue-700 transition-colors disabled:opacity-50"
            data-testid="send-bitrix-report-btn"
          >
            <Building2 className="w-5 h-5" />
            <span>
              {sendBitrixReportMutation.isPending ? 'Отправляем...' : 'Отправить отчёт в Bitrix24'}
            </span>
          </button>
        </div>
      </div>

      {/* Автоматическая отправка отчёта по расписанию */}
      <div className="bg-white rounded-lg border border-gray-200 p-6" data-testid="report-schedule-card">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">Автоматическая отправка отчёта</h3>
        <div className="flex flex-wrap items-end gap-4">
          <div>
            <label className="block text-xs text-gray-500 mb-1">День недели</label>
            <select
              value={scheduleDay}
              onChange={(e) => setScheduleDay(Number(e.target.value))}
              className="px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              data-testid="schedule-day-select"
            >
              {WEEKDAY_OPTIONS.map((opt) => (
                <option key={opt.value} value={opt.value}>{opt.label}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-xs text-gray-500 mb-1">Время (МСК)</label>
            <input
              type="time"
              value={scheduleTime}
              onChange={(e) => setScheduleTime(e.target.value)}
              className="px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              data-testid="schedule-time-input"
            />
          </div>
          {scheduleData?.available_channels?.length > 0 && (
            <div>
              <label className="block text-xs text-gray-500 mb-1">Каналы</label>
              <div className="flex items-center gap-3 py-2">
                {scheduleData.available_channels.map((channel) => (
                  <label key={channel} className="flex items-center gap-1.5 text-sm text-gray-700">
                    <input
                      type="checkbox"
                      checked={selectedChannels.includes(channel)}
                      onChange={() => toggleScheduleChannel(channel)}
                      className="h-4 w-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                      data-testid={`schedule-channel-${channel}`}
                    />
                    {CHANNEL_LABELS[channel] || channel}
                  </label>
                ))}
              </div>
            </div>
          )}
          <button
            onClick={() => saveScheduleMutation.mutate()}
            disabled={saveScheduleMutation.isPending}
            className="bg-gray-900 text-white px-4 py-2 rounded-lg hover:bg-gray-800 transition-colors disabled:opacity-50"
            data-testid="save-schedule-btn"
          >
            {saveScheduleMutation.isPending ? 'Сохраняем...' : 'Сохранить'}
          </button>
        </div>

        <label className="flex items-center gap-2 text-sm text-gray-700 mt-4">
          <input
            type="checkbox"
            checked={scheduleEnabled}
            onChange={(e) => setScheduleEnabled(e.target.checked)}
            className="h-4 w-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
            data-testid="schedule-enabled-checkbox"
          />
          Включить автоматическую отправку
        </label>

        <div className="mt-3 text-sm">
          {!scheduleData?.available_channels?.length ? (
            <p className="text-amber-600">
              Нет подключённых каналов — подключите Telegram или Bitrix24 в разделе «Коннекторы», иначе автоматическая отправка не сработает.
            </p>
          ) : selectedChannels.length === 0 ? (
            <p className="text-amber-600">Выберите хотя бы один канал — иначе отчёт никуда не уйдёт.</p>
          ) : (
            <p className="text-gray-600">
              Отчёт уйдёт в: {selectedChannels.map((c) => CHANNEL_LABELS[c] || c).join(', ')}
            </p>
          )}
          {scheduleData?.last_sent_date && (
            <p className="text-gray-500 mt-1">
              Последняя автоматическая отправка: {formatDate(scheduleData.last_sent_date)} — {SCHEDULE_STATUS_LABELS[scheduleData.last_sent_status] || scheduleData.last_sent_status}
              {scheduleData.last_error && ` (${scheduleData.last_error})`}
            </p>
          )}
        </div>
      </div>

      {/* Основные метрики */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        <StatsCard
          title="Всего клиентов"
          value={dashboardStats?.total_clients || 0}
          icon={Users}
          data-testid="reports-total-clients"
        />
        
        <StatsCard
          title="Активные проекты"
          value={dashboardStats?.active_projects || 0}
          subtitle={`из ${dashboardStats?.total_projects || 0} общих`}
          icon={BarChart3}
          data-testid="reports-active-projects"
        />
        
        <StatsCard
          title="Общая выручка"
          value={formatCurrency(dashboardStats?.total_revenue || 0)}
          icon={TrendingUp}
          data-testid="reports-total-revenue"
        />
        
        <StatsCard
          title="Неоплаченные счета"
          value={dashboardStats?.pending_invoices || 0}
          icon={FileText}
          className={dashboardStats?.pending_invoices > 0 ? "border-yellow-200 bg-yellow-50" : ""}
          data-testid="reports-unpaid-invoices"
        />
      </div>

      {/* Графики */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Месячная выручка */}
        <div className="bg-white rounded-lg border border-gray-200 p-6" data-testid="monthly-revenue-chart">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">
            Месячная выручка (2025)
          </h3>
          {revenueLoading ? (
            <div className="h-64 flex items-center justify-center">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
            </div>
          ) : (
            <ResponsiveContainer width="100%" height={300}>
              <BarChart data={monthlyRevenue || []}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis 
                  dataKey="month" 
                  tick={{ fontSize: 12 }}
                  angle={-45}
                  textAnchor="end"
                  height={60}
                />
                <YAxis tick={{ fontSize: 12 }} />
                <Tooltip 
                  formatter={(value) => [formatCurrency(value), 'Выручка']}
                  labelFormatter={(label) => `Месяц: ${label}`}
                />
                <Bar dataKey="revenue" fill="#3B82F6" />
              </BarChart>
            </ResponsiveContainer>
          )}
        </div>

        {/* Распределение проектов по статусам */}
        <div className="bg-white rounded-lg border border-gray-200 p-6" data-testid="project-distribution-chart">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">
            Проекты по статусам
          </h3>
          {distributionLoading ? (
            <div className="h-64 flex items-center justify-center">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
            </div>
          ) : (projectDistribution && projectDistribution.length > 0) ? (
            <ResponsiveContainer width="100%" height={300}>
              <PieChart>
                <Pie
                  data={projectDistribution}
                  cx="50%"
                  cy="50%"
                  labelLine={false}
                  label={({ status, count }) => 
                    `${status}: ${count}`
                  }
                  outerRadius={80}
                  fill="#8884d8"
                  dataKey="count"
                >
                  {projectDistribution.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                  ))}
                </Pie>
                <Tooltip />
              </PieChart>
            </ResponsiveContainer>
          ) : (
            <div className="py-8 text-center text-gray-500">
              Нет данных для отображения
            </div>
          )}
        </div>
      </div>

      {/* Топ клиентов по выручке */}
      <div className="bg-white rounded-lg border border-gray-200 p-6" data-testid="top-clients-revenue">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">
          Топ клиентов по выручке
        </h3>
        {clientRevenueLoading ? (
          <div className="py-8 flex items-center justify-center">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
          </div>
        ) : (
          <div className="space-y-4">
            {clientRevenue && clientRevenue.length > 0 ? (
              clientRevenue.map((client, index) => (
                <div key={client.client_id} className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
                  <div className="flex items-center space-x-4">
                    <div className="w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center">
                      <span className="text-sm font-medium text-blue-600">
                        {index + 1}
                      </span>
                    </div>
                    <div>
                      <p className="font-medium text-gray-900">{client.client_name}</p>
                    </div>
                  </div>
                  <div className="text-right">
                    <p className="font-semibold text-gray-900">
                      {formatCurrency(client.total_revenue)}
                    </p>
                    <p className="text-sm text-gray-500">выручка</p>
                  </div>
                </div>
              ))
            ) : (
              <div className="text-center py-8 text-gray-500">
                Данные о выручке отсутствуют
              </div>
            )}
          </div>
        )}
      </div>

      {/* Дополнительная статистика */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
        <div className="bg-white rounded-lg border border-gray-200 p-6">
          <h4 className="text-md font-semibold text-gray-900 mb-2">
            Средний чек проекта
          </h4>
          <p className="text-2xl font-bold text-blue-600">
            {dashboardStats?.total_projects > 0
              ? formatCurrency((dashboardStats?.total_revenue || 0) / dashboardStats.total_projects)
              : formatCurrency(0)
            }
          </p>
        </div>

        <div className="bg-white rounded-lg border border-gray-200 p-6">
          <h4 className="text-md font-semibold text-gray-900 mb-2">
            Ожидается к поступлению
          </h4>
          <p className="text-2xl font-bold text-green-600">
            {formatCurrency(dashboardStats?.pending_revenue || 0)}
          </p>
        </div>

        <div className="bg-white rounded-lg border border-gray-200 p-6">
          <h4 className="text-md font-semibold text-gray-900 mb-2">
            Просрочено платежей
          </h4>
          <p className="text-2xl font-bold text-red-600">
            {formatCurrency(dashboardStats?.overdue_amount || 0)}
          </p>
        </div>

        <div className="bg-white rounded-lg border border-gray-200 p-6">
          <h4 className="text-md font-semibold text-gray-900 mb-2">
            Платежи на сегодня
          </h4>
          <p className="text-2xl font-bold text-purple-600">
            {formatCurrency(dashboardStats?.today_payments_amount || 0)}
          </p>
          <p className="text-sm text-gray-500 mt-1">
            {dashboardStats?.today_payments_count || 0} платеж(ей)
          </p>
        </div>
      </div>

      {/* Детальная аналитика по проектам */}
      <div className="bg-white rounded-lg border border-gray-200 p-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-6">Детальная аналитика по проектам</h3>
        
        {/* Фильтры */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Тип отчета</label>
            <select
              value={filterType}
              onChange={(e) => {
                setFilterType(e.target.value);
                setSelectedManager('');
                setSelectedProject('');
              }}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="all">Все проекты (Сводный)</option>
              <option value="manager">По проект-менеджеру</option>
              <option value="project">По конкретному проекту</option>
            </select>
          </div>

          {filterType === 'manager' && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Проект-менеджер</label>
              <select
                value={selectedManager}
                onChange={(e) => setSelectedManager(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                <option value="">Выберите менеджера</option>
                <option value="Прыгункова Елена">Прыгункова Елена</option>
                <option value="Гарасюта Александр">Гарасюта Александр</option>
              </select>
            </div>
          )}

          {filterType === 'project' && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Проект</label>
              <select
                value={selectedProject}
                onChange={(e) => setSelectedProject(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                <option value="">Выберите проект</option>
                {projectsData?.map((project) => (
                  <option key={project.id} value={project.id}>
                    {project.name}
                  </option>
                ))}
              </select>
            </div>
          )}

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Период с</label>
            <input
              type="date"
              value={dateFrom}
              max={dateTo}
              onChange={(e) => setDateFrom(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              data-testid="report-date-from"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Период по</label>
            <input
              type="date"
              value={dateTo}
              min={dateFrom}
              onChange={(e) => setDateTo(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              data-testid="report-date-to"
            />
          </div>
        </div>

        {/* Результаты аналитики */}
        {analyticsLoading ? (
          <div className="flex items-center justify-center py-12">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
          </div>
        ) : detailedAnalytics ? (
          <div className="space-y-6">
            {/* Заголовок отчета */}
            <div className="border-b pb-4">
              <h4 className="text-xl font-semibold text-gray-900">
                {filterType === 'all' && 'Сводный отчет по всем проектам'}
                {filterType === 'manager' && `Отчет по проект-менеджеру: ${detailedAnalytics.manager_name || selectedManager}`}
                {filterType === 'project' && `Отчет по проекту: ${detailedAnalytics.project_name || 'Не указан'}`}
              </h4>
              <p className="text-sm text-gray-600 mt-1">
                Период: {formatDate(dateFrom)} — {formatDate(dateTo)}
              </p>
            </div>

            {/* Метрики */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
              <div className="bg-blue-50 rounded-lg p-4 border border-blue-200">
                <p className="text-sm font-medium text-blue-900">Месячная выручка</p>
                <p className="text-2xl font-bold text-blue-700 mt-2">
                  {formatCurrency(detailedAnalytics.monthly_revenue || 0)}
                </p>
              </div>

              <div className="bg-green-50 rounded-lg p-4 border border-green-200">
                <p className="text-sm font-medium text-green-900">Средний чек</p>
                <p className="text-2xl font-bold text-green-700 mt-2">
                  {formatCurrency(detailedAnalytics.average_check || 0)}
                </p>
              </div>

              <div className="bg-yellow-50 rounded-lg p-4 border border-yellow-200">
                <p className="text-sm font-medium text-yellow-900">Ожидание платежей</p>
                <p className="text-2xl font-bold text-yellow-700 mt-2">
                  {formatCurrency(detailedAnalytics.pending_payments || 0)}
                </p>
              </div>

              <div className="bg-red-50 rounded-lg p-4 border border-red-200">
                <p className="text-sm font-medium text-red-900">Просрочено платежей</p>
                <p className="text-2xl font-bold text-red-700 mt-2">
                  {formatCurrency(detailedAnalytics.overdue_payments || 0)}
                </p>
              </div>
            </div>

            {/* Дополнительная информация */}
            <div className="bg-gray-50 rounded-lg p-4">
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                {filterType === 'all' && (
                  <div>
                    <p className="text-sm text-gray-600">Всего проектов</p>
                    <p className="text-lg font-semibold text-gray-900 mt-1">
                      {detailedAnalytics.total_projects || 0}
                    </p>
                  </div>
                )}
                {filterType === 'manager' && (
                  <div>
                    <p className="text-sm text-gray-600">Проектов менеджера</p>
                    <p className="text-lg font-semibold text-gray-900 mt-1">
                      {detailedAnalytics.total_projects || 0}
                    </p>
                  </div>
                )}
                <div>
                  <p className="text-sm text-gray-600">Общая выручка за период</p>
                  <p className="text-lg font-semibold text-gray-900 mt-1">
                    {formatCurrency(detailedAnalytics.total_revenue || 0)}
                  </p>
                </div>
                <div>
                  <p className="text-sm text-gray-600">Всего счетов</p>
                  <p className="text-lg font-semibold text-gray-900 mt-1">
                    {detailedAnalytics.total_invoices || 0}
                  </p>
                </div>
                <div>
                  <p className="text-sm text-gray-600">Оплаченных счетов</p>
                  <p className="text-lg font-semibold text-gray-900 mt-1">
                    {detailedAnalytics.paid_invoices || 0}
                  </p>
                </div>
              </div>
            </div>

            {/* Проекты менеджера */}
            {filterType === 'manager' && detailedAnalytics.projects && detailedAnalytics.projects.length > 0 && (
              <div>
                <h5 className="text-lg font-semibold text-gray-900 mb-4">Проекты менеджера</h5>
                <div className="overflow-x-auto">
                  <table className="min-w-full divide-y divide-gray-200">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Название</th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Статус</th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Бюджет</th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Выручка</th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                      {detailedAnalytics.projects.map((project) => (
                        <tr key={project.id}>
                          <td className="px-4 py-3 text-sm text-gray-900">{project.name}</td>
                          <td className="px-4 py-3 text-sm">
                            <span className="px-2 py-1 text-xs rounded-full bg-blue-100 text-blue-800">
                              {getStatusLabel(project.status)}
                            </span>
                          </td>
                          <td className="px-4 py-3 text-sm text-gray-900">{formatCurrency(project.budget || 0)}</td>
                          <td className="px-4 py-3 text-sm font-semibold text-green-600">
                            {formatCurrency(project.revenue || 0)}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}

            {/* Распределение по менеджерам для сводного отчета */}
            {filterType === 'all' && detailedAnalytics.projects_by_manager && (
              <div>
                <h5 className="text-lg font-semibold text-gray-900 mb-4">Распределение проектов по менеджерам</h5>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  {Object.entries(detailedAnalytics.projects_by_manager).map(([manager, count]) => (
                    <div key={manager} className="bg-gray-50 rounded-lg p-4 border border-gray-200">
                      <p className="text-sm text-gray-600">{manager}</p>
                      <p className="text-xl font-bold text-gray-900 mt-1">{count} проектов</p>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        ) : (
          <div className="text-center py-12 text-gray-500">
            Выберите фильтры для просмотра аналитики
          </div>
        )}
      </div>

      {/* Анализ оттока клиентов (Churn Analysis) */}
      <div className="bg-white rounded-lg border border-gray-200 p-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-6">Анализ оттока клиентов</h3>
        
        {/* Фильтры */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Период группировки</label>
            <select
              value={churnPeriod}
              onChange={(e) => setChurnPeriod(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="monthly">По месяцам</option>
              <option value="yearly">По годам</option>
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Количество периодов</label>
            <select
              value={churnMonths}
              onChange={(e) => setChurnMonths(Number(e.target.value))}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value={6}>6 периодов</option>
              <option value={12}>12 периодов</option>
              <option value={24}>24 периода</option>
            </select>
          </div>
        </div>

        {/* Результаты */}
        {churnLoading ? (
          <div className="flex items-center justify-center py-12">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
          </div>
        ) : churnData ? (
          <div className="space-y-6">
            {/* Общая статистика */}
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
              <div className="bg-green-50 rounded-lg p-4 border border-green-200">
                <p className="text-sm font-medium text-green-900">Новых клиентов</p>
                <p className="text-2xl font-bold text-green-700 mt-2">{churnData.total_new}</p>
              </div>
              
              <div className="bg-red-50 rounded-lg p-4 border border-red-200">
                <p className="text-sm font-medium text-red-900">Отвалились</p>
                <p className="text-2xl font-bold text-red-700 mt-2">{churnData.total_churned}</p>
              </div>
              
              <div className="bg-yellow-50 rounded-lg p-4 border border-yellow-200">
                <p className="text-sm font-medium text-yellow-900">Приостановлены</p>
                <p className="text-2xl font-bold text-yellow-700 mt-2">{churnData.total_suspended}</p>
              </div>
              
              <div className="bg-blue-50 rounded-lg p-4 border border-blue-200">
                <p className="text-sm font-medium text-blue-900">Процент оттока</p>
                <p className="text-2xl font-bold text-blue-700 mt-2">{churnData.churn_rate}%</p>
              </div>
            </div>

            {/* График */}
            {churnData.data && churnData.data.length > 0 && (
              <div className="bg-gray-50 rounded-lg p-4">
                <h4 className="text-md font-semibold text-gray-900 mb-4">Динамика по периодам</h4>
                <ResponsiveContainer width="100%" height={300}>
                  <BarChart data={churnData.data}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="period" />
                    <YAxis />
                    <Tooltip />
                    <Bar dataKey="new_clients" fill="#10b981" name="Новые" />
                    <Bar dataKey="churned_clients" fill="#ef4444" name="Отвалились" />
                    <Bar dataKey="suspended_clients" fill="#f59e0b" name="Приостановлены" />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            )}

            {/* Таблица детализации */}
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Период</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Новые</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Отвалились</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Приостановлены</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Рост</th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {churnData.data?.map((row, index) => (
                    <tr key={index}>
                      <td className="px-4 py-3 text-sm text-gray-900">{row.period}</td>
                      <td className="px-4 py-3 text-sm font-semibold text-green-600">{row.new_clients}</td>
                      <td className="px-4 py-3 text-sm font-semibold text-red-600">{row.churned_clients}</td>
                      <td className="px-4 py-3 text-sm font-semibold text-yellow-600">{row.suspended_clients}</td>
                      <td className="px-4 py-3 text-sm font-semibold">
                        <span className={row.net_growth >= 0 ? 'text-green-600' : 'text-red-600'}>
                          {row.net_growth >= 0 ? '+' : ''}{row.net_growth}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        ) : null}
      </div>
    </div>
  );
}